import type { SxProps, Theme } from '@mui/material';
import type { DateRangeFilter } from '@/components/DatePicker';
import type { CoverageType } from '../LeaseDetails.accordion/Screening';
import type { Applicant, StepStatus } from './stepper.types';

export enum ExportMenuOptions {
  FilteredRows = 'filtered-rows',
  AllRows = 'all-rows',
}

export enum ApplicationTabs {
  InProgress = 'In progress',
  BondIssued = 'Bonds issued',
}

export interface FilterDataType {
  id: string;
  name: string | string[];
  label?: string;
}

export interface LeaseDateFilters {
  startDateRange: DateRangeFilter;
  endDateRange: DateRangeFilter;
}

export const statusData: FilterDataType[] = [
  { id: 'invitation-sent', label: 'Invitation sent', name: ['invitation-sent'] },
  {
    id: 'renewal-created',
    label: 'Confirm lease renewal',
    name: ['confirm-lease-renewal', 'renewal-created'],
  },
  {
    id: 'lease-details',
    label: 'Provide lease details',
    name: ['pending-lease-details', 'qc-issued', 'approved'],
  },
  {
    id: 'in-progress',
    label: 'Application in progress',
    name: [
      'questionnaire-started',
      'account-created',
      'requested-questionnaire',
      'questionnaire-completed',
      'uploading-documents',
      'pending-docs',
      'pending-visa',
      'pending-income',
      'pending-cotenant-application',
      'unenrolled',
      'renewal-uploading-documents',
      'invitation-in-progress',
      'invitation-opened',
      'awaiting-cash-flow-verification',
      'processing-cash-flow-verification',
    ],
  },
  {
    id: 'pending-approval-decision',
    label: 'Pending approval decision',
    name: ['application-submitted', 'renewal-submitted', 'created'],
  },
  {
    id: 'pending-payment-signature',
    label: 'Pending Payment/Signature',
    name: ['signature-payment-needed', 'quote-ready', 'documents-signed', 'qualified'],
  },
  {
    id: 'denied',
    label: 'Application denied',
    name: [
      'application-denied',
      'denied-by-fico',
      'denied-by-docs',
      'denied-by-cotenant',
      'aborted',
    ],
  },
  {
    id: 'closed',
    label: 'Canceled',
    name: [
      'not-using-theguarantors',
      'qc-not-proceeding',
      'bond-cancelled',
      'closed-error',
      'closed-lost',
    ],
  },
];

// Define the mapping and ordering
export const headerMap: Record<string, string> = {
  lease_id: 'Lease ID',
  lease_start_date: 'Lease Start Date',
  deal_close_date: 'Deal Close Date',
  lease_end_date: 'Lease End Date',
  building_id: 'Building Number',
  building_name: 'Building Name',
  address_street: 'Street Address',
  address_unit: 'Unit Number',
  address_zip: 'Zip Code',
  gross_monthly_rent: 'Rent',
  deal_id: 'Deal ID',
  monolith_applicant_id: 'Applicant ID',
  applicant_first_name: 'First Name',
  applicant_last_name: 'Last Name',
  applicant_email: 'Email Address',
  co_tenants: 'Co-tenants',
  application_status: 'Application Status',
  lg_premium_amount: 'Rent Coverage Amount',
  coverage_months: 'Rent Coverage Months',
  sdr_premium_amount: 'Deposit Coverage Amount',
  sdr_coverage_months: 'Deposit Coverage Months',
  source: 'Source',
  bond_number: 'Bond Number',
};

// Define the desired order
export const inProgressTabOrder: string[] = [
  'application_status',
  'applicant_first_name',
  'applicant_last_name',
  'applicant_email',
  'address_unit',
  'co_tenants',
  'building_id',
  'building_name',
  'address_street',
  'address_zip',
  'lease_start_date',
  'lease_end_date',
  'gross_monthly_rent',
  'coverage_months',
  'lg_premium_amount',
  'sdr_coverage_months',
  'sdr_premium_amount',
];

// Define the desired order
export const bondIssuedTabOrder: string[] = [
  'bond_number',
  'deal_close_date',
  'address_unit',
  'building_id',
  'building_name',
  'address_street',
  'address_zip',
  'applicant_first_name',
  'applicant_last_name',
  'applicant_email',
  'co_tenants',
  'lease_start_date',
  'lease_end_date',
  'coverage_months',
  'lg_premium_amount',
  'sdr_coverage_months',
  'sdr_premium_amount',
];

export type GetDealDocumentHTTPHandlerResponseData = {
  method: string;
  url: string;
};

export type GetDetailedDealDocumentHTTPHandlerResponse = {
  status_code: number;
  message: string;
  data: DetailedLandlordDeal;
};

export type GetDealDocumentHTTPHandlerResponse = {
  status_code: number;
  message: string;
  data: GetDealDocumentHTTPHandlerResponseData;
};

export type LandlordBuilding = {
  building_id: number;
  building_name: string;
};

export type GetLandlordBuildingsHTTPHandlerResponsePagination = {
  current_page: number;
  total_items: number;
  total_pages: number;
  items_per_page: number;
};

export type GetLandlordBuildingsHTTPHandlerResponseData = {
  buildings: LandlordBuilding[];
  buildings_count: number;
  pagination: GetLandlordBuildingsHTTPHandlerResponsePagination;
};

export type GetLandlordBuildingsHTTPHandlerResponse = {
  status_code: number;
  message: string;
  data: GetLandlordBuildingsHTTPHandlerResponseData;
};

export type LandlordDeal = {
  address_city: string | null | undefined;
  address_state: string | null | undefined;
  address_street: string | null | undefined;
  address_unit: string | null | undefined;
  address_zip: string | null | undefined;
  applicant_email: string | undefined;
  applicant_first_name: string | undefined;
  applicant_last_name: string | undefined;
  applicant_phone?: string;
  application_status: string | undefined;
  application_status_label: string | undefined;
  application_status_label_id: ApplicationStatusLabelID | undefined;
  bond_download_url?: string | null;
  bond_number?: string | null;
  building_id: number | undefined;
  building_name: string | undefined;
  close_date: string | undefined;
  co_tenants?: LandlordDeal[] | null;
  coverage_months: string | undefined;
  created_at?: string;
  free_months?: number;
  free_rent?: number;
  gross_monthly_rent: number | null | undefined;
  internal_renewal_from_different_company?: false;
  is_main_tenant?: boolean;
  landlord_name: string | undefined;
  lease_end_date: string | undefined;
  lease_id: number | undefined;
  lease_start_date: string | undefined;
  lg_coverage_amount: number | undefined;
  lg_premium_amount: number | undefined;
  monolith_applicant_id: number | null | undefined;
  monolith_payment_state?: null;
  msa_applicant_id: number | null | undefined;
  msa_payment_state?: null;
  payment_premium?: null;
  payment_remaining?: null;
  payment_status?: null;
  property_id?: number;
  prepaid_rent?: number;
  renew_parent_application_id?: string;
  sdr_coverage_months: string | undefined;
  sdr_premium_amount: number | undefined;
  signature_date?: string;
  signature_status?: null;
  source: string;
  unregistered_emails: string[] | null;
  updated_at?: string;

  monolith_application_id: number | null | undefined;
  monolith_deal_id: number | null | undefined;
  msa_application_id: number | null | undefined;
  msa_deal_id: number | null | undefined;
};

export type ApplicationStatusLabelID =
  | 'canceled-not-using-theguarantors'
  | 'pending-approval-decision'
  | 'pending-payment-signature'
  | 'application-in-progress'
  | 'confirm-lease-renewal'
  | 'provide-lease-details'
  | 'application-denied'
  | 'invitation-sent'
  | 'deal-closed';

export type ApplicationStatusLabelIDCategory = 'in-progress' | 'landlord-action-needed' | 'outcome';

export const applicationStatusLabelIDCategoryMap: Record<
  ApplicationStatusLabelID,
  ApplicationStatusLabelIDCategory
> = {
  'pending-payment-signature': 'in-progress',
  'pending-approval-decision': 'in-progress',
  'application-in-progress': 'in-progress',
  'invitation-sent': 'in-progress',

  'provide-lease-details': 'landlord-action-needed',
  'confirm-lease-renewal': 'landlord-action-needed',

  'canceled-not-using-theguarantors': 'outcome',
  'application-denied': 'outcome',
  'deal-closed': 'outcome',
};

export const getApplicationStatusColorByCategory = (
  category: ApplicationStatusLabelIDCategory,
  theme: Theme,
  featureFlags: {
    leaseDetailsAccordionEnabled: boolean;
  },
): { textColor: string; indicatorColor: string } => {
  const { leaseDetailsAccordionEnabled } = featureFlags;

  const colorMap: Record<
    ApplicationStatusLabelIDCategory,
    { textColor: string; indicatorColor: string }
  > = {
    'in-progress': {
      textColor: theme.palette.text.primary,
      indicatorColor: theme.palette.indigo[900] as string,
    },
    'landlord-action-needed': {
      textColor: theme.palette.text.primary,
      indicatorColor: leaseDetailsAccordionEnabled
        ? (theme.palette.orange[500] as string)
        : ((theme.palette.yellow?.legacy?.[500] ?? theme.palette.yellow[500]) as string),
    },
    outcome: {
      textColor: theme.palette.text.primary,
      indicatorColor: (theme.palette.red?.legacy?.[400] ?? theme.palette.red[400]) as string,
    },
  };

  return colorMap[category];
};

export const getCategoryBasedHoverStyle = (
  category: ApplicationStatusLabelIDCategory,
  theme: Theme,
  additionalStyles?: SxProps<Theme>,
) => {
  switch (category) {
    case 'landlord-action-needed':
      return {
        backgroundColor: `${theme?.palette?.yellow?.legacy?.[100] ?? theme?.palette?.yellow[100]} !important`,
        ...additionalStyles,
      };
    default:
      return { backgroundColor: theme?.palette?.action?.hover };
  }
};

export type DealQuery = {
  id?: string;
  source?: string;
};

export type LeaseDetails = {
  address: string;
  building_id: number;
  deal_id: number;
  deal_source: string;
  deposit_coverage_override: number | null;
  free_rent: number | null;
  gross_monthly_rent: number;
  lease_end_date: string;
  lease_start_date: string;
  prepaid_rent: number | null;
  rent_coverage_override: number | null;
  screening_result: ScreeningResult | null;
  screening_result_label?: string | null;
  submitted_at: string;
  submitted_by_email: string;
  unit: string | null;
  has_custom_rent_coverage?: boolean;
  has_custom_deposit_coverage?: boolean;
  manual_underwriting?: boolean;
  renters: LeaseDetailsRenter[];
  is_pending_review: boolean;
};

export type TerminationStatus = 'denied' | 'canceled' | 'none';

export type DetailedLandlordDeal = {
  applicants: Applicant[];
  application_status_label?: string;
  application_status_label_id?: ApplicationStatusLabelID;
  chip_status: string;
  lease_details: LeaseDetails;
  payment_premium: string | number;
  payment_remaining: string | number;
  step_status: StepStatus[];
  termination_status: TerminationStatus;
};

export interface ExportDealsParams {
  applicationTab: string;
  allRecords: boolean;
  limit?: number;
  lastTimestamp?: number | null;
  search?: string;
  selectedStatus?: FilterDataType[];
  selectedBuildings?: FilterDataType[];
  leaseDates?: LeaseDateFilters;
}

export type GetLandlordDealsHTTPHandlerResponse = {
  status_code: number;
  message: string;
  data: GetLandlordDealsHTTPHandlerResponseData;
};

export type GetLandlordDealsPaginationResponseData = {
  total_items: number;
  total_page: number;
  item_per_page: number;
};

export type GetLandlordDealsExportHTTPHandlerResponse = {
  status_code: number;
  message: string;
  data: GetLandlordDealsExport;
};

export type GetLandlordDealsExport = {
  exported_file_url: string;
  exported_at_timestamp: string;
  exported_deals_count: number;
};

export type GetLandlordDealsHTTPHandlerResponseData = {
  deals: LandlordDeal[];
  pagination: GetLandlordDealsHTTPHandlerResponsePagination;
};

export type GetLandlordDealsHTTPHandlerResponsePagination = {
  total_items: number;
  total_pages: number;
  items_per_page: number;
};

export type GetLandlordDealsHTTPHandlerQueryParams = {
  pagination: GetLandlordDealsPagination;
  filters: GetLandlordDealsWhereClauses;
};

export type GetLandlordDealsPagination = {
  limit?: number | null;
  last_seen_timestamp?: number | null;
};

export type GetLandlordDealsWhereClauses = {
  unit?: string | null;
  applicant_first_name?: string | null;
  applicant_last_name?: string | null;
  applicant_email?: string | null;
  deal_status?: string | null;
};

export type DealSource = 'atlas' | 'monolith';
export type SMBCoverage = 'low' | 'medium' | 'high' | 'custom';
export type Institutional = 'approved' | 'conditionally_approved' | 'denied';
export type CoverageAndScreening = Institutional | SMBCoverage;
export type ScreeningResult = 'Approved' | 'Conditional' | 'Denied' | 'Custom' | '';

export type SelectValues = {
  value: string;
  label: string;
  description: string;
  tooltip?: React.ReactNode;
};

export type LeaseDetailsRenter = {
  id?: string;
  name: string;
  email: string;
  is_main_renter: boolean;
};

export type LeaseDetailsFormBase = {
  renters: LeaseDetailsRenter[];
  address: string;
  unit: string;
  lease_start_date: string;
  lease_end_date: string;
  gross_monthly_rent: string;
  screening_result: ScreeningResult;
  prepaid_rent?: string;
  free_rent?: string;
  rent_coverage_override?: string;
  deposit_coverage_override?: string;
  building_id: number;
};

export type LeaseDetailsFormData = LeaseDetailsFormBase & {
  rent_coverage_override_type?: CoverageType;
  deposit_coverage_override_type?: CoverageType;
  property_id?: string;
};

export type LeaseDetailsFormKeys = keyof LeaseDetailsFormData;
export type LeaseDetailsFormScalarKeys = Exclude<LeaseDetailsFormKeys, 'renters'>;
export type LeaseDetailsFormCoverageKeys =
  | 'deposit_coverage_override_type'
  | 'rent_coverage_override_type';
